import { createSelector } from "reselect";

import _isEmpty from "lodash-es/isEmpty";

import { getPath as getInstallIconPath } from "Clients/Icons/Install";
import * as ImagesClient from "Clients/Images";

import RootState from "Store/Root";

import { DesignState } from "Store/Designs/Reducer";
import designInstallsSelector from "Store/Designs/Selectors/DesignInstalls";

import { RoomsState } from "Store/Rooms/Reducer";
import { getValidRooms } from "Store/Rooms/Utils";
import * as TrykUtils from "Utils/Tryk";
import {
  AvailableItemOptions,
  BasketItemColorway,
  BasketItemDisplay,
  ColorwayState,
  DesignInstallOption,
  RoomSceneOption,
  TrykProductOption
} from "Types/Basket";

import { mapAndSortWithTileSize } from "Utils/DesignInstalls";
import { serializeRgbs } from "Utils/Rgb";
import { filterColorwayProducts } from "Utils/Tryk";
import { serializeYarnCodes } from "Utils/Yarns";

import { BasicItemOptions, basicOptionsSelector } from "./BasicOptions";

const colorwayStatesSelector = (state: RootState): ColorwayState[] => {
  const { colorwaysById, itemsList } = state.basket;

  return itemsList.map(x => colorwaysById[x]).filter(x => !_isEmpty(x));
};

export const hasColorwaysLoadedSelector = createSelector(
  colorwayStatesSelector,
  (state: RootState) => state.designs.itemsByCode,
  (items: ColorwayState[], designs: _.Dictionary<DesignState>): boolean => {
    return items.every(x => !!designs[x.colorway.designCode.toUpperCase()]);
  }
);

export const colorwayItemsSelector = createSelector(
  hasColorwaysLoadedSelector,
  (state: RootState) => state,
  colorwayStatesSelector,
  basicOptionsSelector,
  (
    hasLoaded: boolean,
    state: RootState,
    items: ColorwayState[],
    basicOptions: BasicItemOptions
  ): BasketItemColorway[] => {
    const result =
      (hasLoaded ? items : []).map(
        (item): BasketItemColorway => ({
          id: item.id,
          colorway: item.colorway,
          display: getBasketColorwayDisplay(item.colorway),
          install: getDesignInstalls(state, item.colorway.designCode, true).find(
            x => x.installId === item.installId
          ),
          printScale: basicOptions.printScales.find(
            x => x.value === item.printScale
          ),
          brand: getBrand(item, basicOptions),
          product: getTrykProductOption(
            state.trykLayouts.itemsById[item.productId.toString()]
          ),
          room: getDesignRooms(
            state.rooms,
            state.designs.itemsByCode,
            item.colorway.designCode
          ).find(x => x.roomId === item.roomId),
          swatchOffset: {
            ...item.swatchOffset
          }
        })
      );

    return result;
  }
);

export function getAvailableOptions(
  item: BasketItemColorway,
  state: RootState
): AvailableItemOptions {
  const options = basicOptionsSelector(state);
  const rooms = getDesignRooms(
    state.rooms,
    state.designs.itemsByCode,
    item.colorway.designCode
  );

  const sectionIds = TrykUtils.getSections(state, item.colorway.designCode);

  const products = filterColorwayProducts(
    state.trykLayouts,
    item.colorway.brandId,
    rooms.length > 0,
    sectionIds
  );

  return {
    installs: getDesignInstalls(state, item.colorway.designCode, false),
    printScales: options.printScales,
    rooms,
    brands: options.brands,
    products: products.map(x => getTrykProductOption(x))
  };
}

function getDesignInstalls(
  state: RootState,
  designCode: string,
  useBrandInstalls: boolean
): DesignInstallOption[] {
  const designInstalls = designInstallsSelector(state, { designCode, useBrandInstalls });

  if (!designInstalls || designInstalls.length === 0) {
    return [];
  }

  const sorted = mapAndSortWithTileSize(designInstalls);

  return sorted.map(x => ({
    ...x,
    thumbnailUrl: getInstallIconPath(x)
  }));
}

function getDesignRooms(
  state: RoomsState,
  store: _.Dictionary<DesignState>,
  designCode: string
): RoomSceneOption[] {
  const design = store[designCode.toUpperCase()];

  if (!design || !design.overview || !state) {
    return [];
  }

  return getValidRooms(
    state,
    design.overview.brandId,
    design.overview.sectionIds,
    false, // TODO: Implement custom rug behavior here.
    undefined
  ).map(x => ({
    ...x,
    thumbnailUrl: ImagesClient.Urls.Thumbnail.room(x.roomId)
  }));
}

function getBasketColorwayDisplay(
  input: TrykApi.Catalog.IColorway
): BasketItemDisplay {
  const getThumbnailUrl = (x: TrykApi.Catalog.IColorway): string => {
    if (input && input.clutColorwayId) {
      return ImagesClient.Urls.Repeat.recolor({
        designCode: x.designCode,
        rgbs: x.rgbs.map(({ auto, r, g, b }) => "x"),
        yarns: serializeYarnCodes(x.yarns),
        refColor: "",
        pixels: 100,
        square: true,
        clutColorwayId: input.clutColorwayId
      });
    }
    if (x.colorwayId > 0) {
      return ImagesClient.Urls.Thumbnail.colorway(x.designCode, x.colorCode);
    } else {
      return ImagesClient.Urls.Repeat.recolor({
        designCode: x.designCode,
        rgbs: serializeRgbs(x.rgbs),
        yarns: serializeYarnCodes(x.yarns),
        refColor: x.colorCode,
        pixels: 100,
        square: true,
        clutColorwayId: input.clutColorwayId
      });
    }
  };

  return {
    primaryDisplay: input.designCode,
    secondaryDisplay:
      input.designName && input.designCode !== input.designName
        ? input.designName
        : "",
    thumbnailUrl: getThumbnailUrl(input)
  };
}

function getTrykProductOption(
  item: TrykApi.Catalog.ITrykProduct
): TrykProductOption {
  return {
    ...item,
    thumbnailUrl: ImagesClient.Urls.Thumbnail.trykProduct(item.productId),
    hasSwatch: !!item.frames.find(x => x.frameContentTypeId === 1),
    hasRoom: !!item.frames.find(x => x.frameContentTypeId === 2)
  };
}

function getBrand(
  item: ColorwayState,
  basicOptions: BasicItemOptions
): TrykApi.Catalog.ISiteBrand {

  if (item.layoutBrandId === -1) {
    return {
      brandId: -1,
      name: '',
    };
  }

  return basicOptions.brands.find(x => x.brandId === item.layoutBrandId) ||
    basicOptions.brands.find(x => x.brandId === item.colorway.brandId);
}
